import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { createSuccessToast } from '@adflow/frontend/ui-toasts';
import { apiRequest, handleRequestErrors } from '@adflow/frontend/util-query';
import type {
	AddGoogleAdsTemplateDto,
	EditGoogleAdsTemplateDto,
} from '@adflow/shared/data-google-ads';
import {
	AddGoogleAdsTemplate,
	ArchiveGoogleAdsTemplate,
	DeleteGoogleAdsTemplate,
	DeleteGoogleAdsTemplates,
	GetGoogleAdsConversionGoals,
	GetGoogleAdsTemplateAudienceSegments,
	GetGoogleAdsTemplateEnums,
	GetGoogleAdsTemplateMerchantCenterFeeds,
	GetGoogleAdsTemplateUploadProgress,
	GetGoogleMyBusinessLocations,
	UploadGoogleAdsTemplate,
} from '@adflow/shared/data-google-ads';
import type {
	ListGoogleAdsTemplatesHistoryQuery,
	ListGoogleAdsTemplatesQuery,
} from '@adflow/shared/util-history';
import {
	EditGoogleAdsTemplate,
	GetGoogleAdsTemplate,
	GetGoogleAdsTemplateHistory,
	ListGoogleAdsTemplatesWithUploadResult,
} from '@adflow/shared/util-history';

export const googleAdsTemplateKeys = {
	all: ['google-ads', 'template'] as const,
	allList: () => [...googleAdsTemplateKeys.all, 'list'] as const,
	list: (query: ListGoogleAdsTemplatesQuery) =>
		[...googleAdsTemplateKeys.allList(), query] as const,
	view: (templateId: number) =>
		[...googleAdsTemplateKeys.all, 'view', templateId] as const,
	enums: (avbIds: string[]) =>
		[
			...googleAdsTemplateKeys.all,
			'enums',
			[...avbIds].sort().join(','),
		] as const,
	gmbLocations: () => [...googleAdsTemplateKeys.all, 'gmb-locations'] as const,
	audienceSegments: (avbIds: string[]) =>
		[
			...googleAdsTemplateKeys.all,
			'audience-segments',
			[...avbIds].sort().join(','),
		] as const,
	merchantCenterFeeds: (avbIds: string[]) =>
		[
			...googleAdsTemplateKeys.all,
			'merchant-center-feeds',
			[...avbIds].sort().join(','),
		] as const,
	history: (templateId: number, query?: ListGoogleAdsTemplatesHistoryQuery) =>
		[...googleAdsTemplateKeys.all, 'history', templateId, query] as const,
	conversionGoals: (avbIds: string[]) =>
		[
			...googleAdsTemplateKeys.all,
			'conversion-goals',
			[...avbIds].sort().join(','),
		] as const,
	uploadProgress: (templateId: number) =>
		[...googleAdsTemplateKeys.all, 'uploadProgress', templateId] as const,
};

export function useListGoogleAdsTemplatesQuery(
	query: ListGoogleAdsTemplatesQuery,
	enabled = true
) {
	return useQuery({
		queryKey: googleAdsTemplateKeys.list(query),
		queryFn: ({ signal }) =>
			apiRequest(ListGoogleAdsTemplatesWithUploadResult, { query }, { signal }),
		enabled,
	});
}

export const fetchGoogleAdsTemplate = async (templateId: number) => {
	return apiRequest(GetGoogleAdsTemplate, { params: { templateId } });
};

export function useGoogleAdsTemplateQuery(templateId: number) {
	return useQuery({
		queryKey: googleAdsTemplateKeys.view(templateId),
		queryFn: ({ signal }) =>
			apiRequest(GetGoogleAdsTemplate, { params: { templateId } }, { signal }),
	});
}

export function useAddGoogleAdsTemplateMutation(duplicated?: boolean) {
	const { t } = useTranslation(['facebook', 'common']);
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: AddGoogleAdsTemplateDto) =>
			apiRequest(AddGoogleAdsTemplate, { body, query: { duplicated } }),
		onSuccess: (template) => {
			queryClient.setQueryData(
				googleAdsTemplateKeys.view(template.id),
				template
			);
			void queryClient.invalidateQueries({
				queryKey: googleAdsTemplateKeys.allList(),
			});
			if (duplicated) {
				createSuccessToast(t('common:templateDuplicatedSuccessfully'));
			} else {
				createSuccessToast(t('facebook:success.savingTemplate'));
			}
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: t('error.savingTemplate'),
			});
		},
	});
}

export function useEditGoogleAdsTemplateMutation() {
	const { t } = useTranslation('facebook');
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			templateId,
			edits,
		}: {
			templateId: number;
			edits: EditGoogleAdsTemplateDto;
		}) =>
			apiRequest(EditGoogleAdsTemplate, {
				params: { templateId },
				body: edits,
			}),
		onSuccess: (template) => {
			queryClient.setQueryData(
				googleAdsTemplateKeys.view(template.id),
				template
			);
			void queryClient.invalidateQueries({
				queryKey: googleAdsTemplateKeys.allList(),
			});
			createSuccessToast(t('success.savingTemplate'));
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: t('error.savingTemplate'),
			});
		},
	});
}

export function useArchiveGoogleAdsTemplateMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (templateId: number) =>
			apiRequest(ArchiveGoogleAdsTemplate, { params: { templateId } }),
		onSuccess: (template) => {
			queryClient.setQueryData(
				googleAdsTemplateKeys.view(template.id),
				template
			);
			void queryClient.invalidateQueries({
				queryKey: googleAdsTemplateKeys.allList(),
			});
		},
	});
}

export function useGoogleAdsEnums(avbIds: string[]) {
	return useQuery({
		queryKey: googleAdsTemplateKeys.enums(avbIds),
		queryFn: ({ signal }) =>
			apiRequest(GetGoogleAdsTemplateEnums, { body: { avbIds } }, { signal }),
	});
}

export function useGoogleMyBusinessLocations() {
	return useQuery({
		queryKey: googleAdsTemplateKeys.gmbLocations(),
		queryFn: ({ signal }) =>
			apiRequest(GetGoogleMyBusinessLocations, {}, { signal }),
		staleTime: Infinity,
	});
}

export function useGoogleAdsTemplateAudienceSegments(
	avbIds: string[],
	enabled = true
) {
	return useQuery({
		queryKey: googleAdsTemplateKeys.audienceSegments(avbIds),
		queryFn: ({ signal }) =>
			apiRequest(
				GetGoogleAdsTemplateAudienceSegments,
				{ query: { avbIds } },
				{ signal }
			),
		enabled: (avbIds?.length ?? 0) > 0 && enabled,
	});
}

export function useGoogleAdsTemplateMerchantCenterFeeds(
	avbIds: string[],
	enabled = true
) {
	return useQuery({
		queryKey: googleAdsTemplateKeys.merchantCenterFeeds(avbIds),
		queryFn: ({ signal }) =>
			apiRequest(
				GetGoogleAdsTemplateMerchantCenterFeeds,
				{ query: { avbIds } },
				{ signal }
			),
		enabled: (avbIds?.length ?? 0) > 0 && enabled,
	});
}

export function useDeleteGoogleAdsTemplateMutation() {
	const { t } = useTranslation('google-ads');
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (templateId: number) =>
			apiRequest(DeleteGoogleAdsTemplate, { params: { templateId } }),
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: googleAdsTemplateKeys.allList(),
			});
			createSuccessToast(t('success.deletingTemplate'));
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: t('error.deletingTemplate'),
			});
		},
	});
}

export function useDeleteGoogleAdsTemplatesMutation() {
	const { t } = useTranslation('google-ads');
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (templateIds: number[]) =>
			apiRequest(DeleteGoogleAdsTemplates, { body: { templateIds } }),
		onSuccess: (_data, templateIds) => {
			void queryClient.invalidateQueries({
				queryKey: googleAdsTemplateKeys.allList(),
			});
			createSuccessToast(
				t('success.deletingTemplate', { count: templateIds.length })
			);
		},
		onError: (err, templateIds) => {
			handleRequestErrors([err], {
				errorMessage: t('error.deletingTemplate', {
					count: templateIds.length,
				}),
			});
		},
	});
}

export function useUploadGoogleAdsTemplateMutation() {
	const { t } = useTranslation('google-ads');

	return useMutation({
		mutationFn: async (templateId: number) =>
			apiRequest(UploadGoogleAdsTemplate, {
				params: { templateId },
			}),
		onSuccess: () => {
			createSuccessToast(t('googleAdsUpload.begin'));
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: t('googleAdsUpload.error'),
			});
		},
	});
}

export function useGoogleAdsTemplateUploadProgressQuery(
	templateId: number,
	enabled = true
) {
	return useQuery({
		queryKey: googleAdsTemplateKeys.uploadProgress(templateId),
		queryFn: ({ signal }) =>
			apiRequest(
				GetGoogleAdsTemplateUploadProgress,
				{ params: { templateId } },
				{ signal }
			),
		enabled,
	});
}

export function useGoogleAdsTemplateHistoryQuery(
	templateId: number,
	query: ListGoogleAdsTemplatesHistoryQuery,
	enabled = true
) {
	return useQuery({
		queryKey: googleAdsTemplateKeys.history(templateId, query),
		queryFn: ({ signal }) =>
			apiRequest(
				GetGoogleAdsTemplateHistory,
				{
					params: { templateId },
					query,
				},
				{ signal }
			),
		enabled,
	});
}

export function useGoogleAdsConversionGoalsQuery(avbIds: string[]) {
	return useQuery({
		queryKey: googleAdsTemplateKeys.conversionGoals(avbIds),
		queryFn: ({ signal }) =>
			apiRequest(
				GetGoogleAdsConversionGoals,
				{ query: { avbIds } },
				{ signal }
			),
	});
}
